import { keys } from 'lodash';

import { assertHasProperty, assertIsOneOfTypes } from 'common/assertions';

import { type Stories, type BlockDict, ComponentType, Block } from 'types';
import { getStory } from './Stories';
import type { StoryStoreState } from 'store/reducers/StoryReducer';

//=============================================================================
// Block getters
//
// Below are functions that are used to retrieve information about one or more
// blocks
//=============================================================================

export const getStoryBlockIds = (storyUid: string, stories: Stories) => {
  const story = getStory(storyUid, stories);

  return story.blockIds;
};

export type BlockWithBlockId = { blockId: string } & Block;

/**
 * Gets all the blocks for a story and puts them in order according to their blockIds.
 * If given blockId isn't there, it's left out.
 */
export const getStoryBlocks = (storyState: StoryStoreState, storyUid: string): BlockWithBlockId[] => {
  const blockIds = getStoryBlockIds(storyUid, storyState.stories);

  return blockIds
    .map((blockId) => {
      const block = storyState.blocks[blockId];

      if (!block) {
        return null;
      }

      return { blockId, ...block };
    })
    .filter((it) => !!it);
};

/**
 * Gets the global filter bar for a given story. If there isn't one, return null.
 * If there are multiple, we throw.
 *
 * NOTE: if somehow there's a block that has multiple GFB components in there, we still return it.
 * This selector is probably not the place to do that validation.
 */
export const getGlobalFilterBar = (blocks: BlockWithBlockId[]): BlockWithBlockId | null => {
  const gfbs = blocks.filter(({ components }) => components[0]?.type === ComponentType.GLOBAL_FILTER);

  if (gfbs.length > 1) {
    throw new Error(`Only one Global Filter Bar expected, but found multiple: ${gfbs}`);
  }

  return gfbs[0] ?? null;
};

export const getStoryBlockAtIndex = (
  storyUid: string,
  index: number,
  stories: Stories,
  blocks: BlockDict,
) => {
  const story = getStory(storyUid, stories);
  const blockIds = story.blockIds;

  if (index < 0 || index >= blockIds.length) {
    throw new Error(
      `\`index\` argument is out of bounds; index: "${index}", blockIds.length: "${blockIds.length}".`,
    );
  }

  return blocks[blockIds[index]];
};

export const getStoryBlockIdAtIndex = (storyUid: string, index: number, stories: Stories) => {
  const story = getStory(storyUid, stories);
  const blockIds = story.blockIds;

  if (index < 0 || index >= blockIds.length) {
    throw new Error(
      `\`index\` argument is out of bounds; index: "${index}", blockIds.length: "${blockIds.length}".`,
    );
  }

  return blockIds[index];
};

export const getBlockLayout = (blockId: string, blocks: BlockDict) => {
  const block = getBlock(blockId, blocks);

  return block.layout;
};

export const getBlock = (blockId: string, blocks: BlockDict) => {
  assertIsOneOfTypes(blockId, 'string');
  assertHasProperty(
    blocks,
    blockId,
    `Block with id ${blockId} does not exist. Existing block IDs: ${keys(blocks)}`,
  );

  return blocks[blockId];
};

export const getBlockComponents = (blockId: string, blocks: BlockDict) => {
  const block = getBlock(blockId, blocks);
  return block.components;
};

export const getBlockBackgroundColor = (blockId: string, blocks: BlockDict) => {
  const block = getBlock(blockId, blocks);
  return block.background_color;
};

export const getBlockComponentAtIndex = (blockId: string, index: number, blocks: BlockDict) => {
  const components = getBlockComponents(blockId, blocks);

  if (index < 0 || index >= components.length) {
    throw new Error(
      `\`index\` argument is out of bounds; index: "${index}", components.length: "${components.length}".`,
    );
  }

  return components[index];
};

//=============================================================================
// Permission helpers
//
// Below are functions that are used to retrieve permission information about
// one or more blocks
//=============================================================================

export const isBlockPresentable = (blockId: string, blocks: BlockDict) => {
  const block = getBlock(blockId, blocks);
  return block.presentable;
};

// Returns true if the given block is restricted to measure card embeds only.
export const isBlockRestrictedToMeasureCardsOnly = (blockId: string, blocks: BlockDict) => {
  return getBlockLayout(blockId, blocks) === 'fluid6';
};
